import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import _, { findIndex, isEqual, takeRight } from 'lodash';

import { SessionStorageUtilities } from '../../../utilities/StorageUtilities';
import { buildUrlParamValue, parseUrlParamValue } from '../../../utilities/FilterStateUrlUtilities';
import { usePlmId } from '../../../utilities/PgmUtilities';
import {
    agentsPageId,
    businessServicesPageId,
    sitesPageId,
    testsPageId,
    serversPageId,
    networkDevicesPageId,
    interfacesPageId,
    wirelessControllersPageId,
    accessPointsPageId,
    radiosPageId,
    hypervisorsPageId,
    virtualMachinesPageId,
} from '../../pages';

const MAX_HISTORY_ITEMS = 5;

const idToLegacyIdMap = {
    [agentsPageId]: 'pulseHistory',
    [businessServicesPageId]: 'businessServiceHistory',
    [sitesPageId]: 'siteHistory',
    [testsPageId]: 'testHistory',
    [serversPageId]: 'serverHistory',
    [networkDevicesPageId]: 'networkDeviceHistory',
    [interfacesPageId]: 'interfaceHistory',
    [wirelessControllersPageId]: 'wirelessControllerHistory',
    [accessPointsPageId]: 'accessPointHistory',
    [radiosPageId]: 'radioHistory',
    [hypervisorsPageId]: 'hypervisorHistory',
    [virtualMachinesPageId]: 'virtualMachineHistory',
};

function stripSlashes(page) {
    return page.replace('/', '');
}

function getHistoryStorageKey(user, pageId, plmId) {
    return `user-${user.id}${plmId ? `-plm-${plmId}` : ''}-history-cfg-${idToLegacyIdMap[pageId]}`;
}

function convertFromLegacyHistoryItem(historyConfig, legacyItem) {
    return {
        title: legacyItem.text,
        link: {
            ...historyConfig.link,
            encodedFilterState: parseUrlParamValue(legacyItem.targetParams.filterState),
        },
    };
}

function convertToLegacyHistoryItem(historyConfig, item) {
    const { plmId } = historyConfig.link;
    const target = plmId ? 'app.auth.plm.page' : 'app.auth.page';
    const filterState = buildUrlParamValue(item.link.encodedFilterState);

    return {
        actions: [{ icon: 'clear', action: 'remove-history-node', title: 'Remove this history item' }],
        active: false,
        activeTargets: [
            {
                filterId: historyConfig.filterId,
                target,
                targetParams: {
                    page: stripSlashes(item.link.target),
                },
                filterParams: {
                    filterState,
                },
            },
        ],
        collapsed: true,
        collapsible: false,
        hover: true,
        icon: 'arrow_forward',
        iconSize: 12,
        indentItems: false,
        showCollapser: false,
        showLink: true,
        target,
        targetParams: {
            plmId,
            page: stripSlashes(item.link.target),
            filterState,
        },
        text: item.title,
    };
}

function getHistoryItems(user, plmId, pageId, historyConfig) {
    return SessionStorageUtilities.getJSONValue(getHistoryStorageKey(user, pageId, plmId), []).map(
        _.partial(convertFromLegacyHistoryItem, historyConfig)
    );
}

function setHistoryItems(user, plmId, pageId, historyConfig, items) {
    SessionStorageUtilities.setJSONValue(
        getHistoryStorageKey(user, pageId, plmId),
        items.map(_.partial(convertToLegacyHistoryItem, historyConfig))
    );
}

export function useHistory(user, pageId, historyConfig) {
    const plmId = usePlmId();

    const [history, dispatch] = useReducer(historyReducer, null, () =>
        getHistoryItems(user, plmId, pageId, historyConfig)
    );

    // keep history items in sync
    useEffect(() => {
        setHistoryItems(user, plmId, pageId, historyConfig, history);
    }, [history, historyConfig, pageId, plmId, user]);

    const actions = useMemo(
        () => ({
            addHistoryItem: (item) => dispatch({ action: 'add', item }),
            removeHistoryItem: (item) => dispatch({ action: 'remove', item }),
            resetHistory: (item) => dispatch({ action: 'reset', item }),
        }),
        []
    );

    return [history, actions];
}

function historyReducer(items, { action, item }) {
    if (action === 'add') {
        if (findHistoryIdx(item) !== -1) {
            return items;
        }

        return takeRight([...items, item], MAX_HISTORY_ITEMS);
    }

    if (action === 'remove') {
        return withoutHistoryItem(item);
    }

    if (action === 'reset') {
        return [];
    }

    return items;

    function findHistoryIdx(historyItem) {
        return findIndex(items, (i) => isEqual(historyItem.link.encodedFilterState, i.link.encodedFilterState));
    }

    function withoutHistoryItem(historyItem) {
        const idx = findHistoryIdx(historyItem);

        return idx === -1 ? items : [...items.slice(0, idx), ...items.slice(idx + 1)];
    }
}

function getExpansionStorageKey(user, plmId, groupId) {
    return `user-${user.id}${plmId ? `-plm-${plmId}` : ''}-${groupId}-expanded`;
}

function getIsExpanded(user, plmId, groupId, defaultState) {
    return SessionStorageUtilities.getJSONValue(getExpansionStorageKey(user, plmId, groupId), defaultState);
}

function setIsExpanded(user, plmId, groupId, expanded) {
    SessionStorageUtilities.setJSONValue(getExpansionStorageKey(user, plmId, groupId), expanded);
}

export function useIsExpanded(user, groupId, defaultState) {
    const plmId = usePlmId();
    const initialState = useMemo(
        () => getIsExpanded(user, plmId, groupId, defaultState),
        [user, plmId, groupId, defaultState]
    );
    const [isExpanded, setExpanded] = useState(initialState);
    const set = useCallback(
        (state) => {
            setIsExpanded(user, plmId, groupId, state);
            setExpanded(state);
        },
        [user, plmId, groupId]
    );

    return [isExpanded, set];
}
